import { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react";
import { HiOutlinePencilSquare } from "react-icons/hi2";
import { useAuth } from "@/auth/AuthContext";
import { toaster } from "@/components/ui/toaster";
import { FormInput, FormTextarea } from "@/components/jobOffer/ui";
import { CandidateService } from "@/services/CandidateService";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import useScoreLabels from "./useScoreLabels";

const formatPoints = (v) => (Number.isInteger(Number(v)) ? String(v) : Number(v).toFixed(1));

/** Normalizes the scale frozen in the result (preferred, so history survives criteria edits) or, for older results, the current model. */
function toScoring(row, modelScoring) {
    if (!row.scale?.length) return modelScoring;
    const t = row.scoringType;
    if (t === "Range") return { scoringType: t, rangeRules: row.scale.map((e) => ({ min: e.min, max: e.max, points: e.points })) };
    if (t === "AiMatch") return { scoringType: t, aiLevels: row.scale.map((e) => ({ level: e.value, points: e.points })) };
    return { scoringType: t, valueRules: row.scale.map((e) => ({ value: e.value, points: e.points })) };
}

/** Scale rows for one criterion: [{ label, points, matched }]. Matching mirrors the backend evaluators (first hit wins). */
function buildScale(scoring, row, { t, aiLevel }) {
    if (!scoring) return [];
    const value = row.capturedValue;
    if (scoring.scoringType === "Value") {
        const hit = (scoring.valueRules ?? []).findIndex((r) => value != null && r.value.toLowerCase() === String(value).toLowerCase());
        return (scoring.valueRules ?? []).map((r, i) => ({ label: r.value, points: r.points, matched: i === hit }));
    }
    if (scoring.scoringType === "Range") {
        const n = Number(String(value ?? "").replace(",", "."));
        const hasNumber = value != null && value !== "" && !Number.isNaN(n);
        const hit = hasNumber ? (scoring.rangeRules ?? []).findIndex((r) => (r.min == null || n >= r.min) && (r.max == null || n <= r.max)) : -1;
        return (scoring.rangeRules ?? []).map((r, i) => ({
            label: r.min == null ? `${t("scoreLabels.rangeUpTo")} ${r.max}` : r.max == null ? `${r.min} ${t("scoreLabels.rangeAndMore")}` : t("scoreLabels.rangeFromTo", { from: r.min, to: r.max }),
            points: r.points,
            matched: i === hit,
        }));
    }
    if (scoring.scoringType === "AiMatch") {
        return (scoring.aiLevels ?? []).map((l) => ({ label: aiLevel(l.level), points: l.points, matched: String(row.aiLevel ?? "").replace(/_/g, "").toLowerCase() === String(l.level).replace(/_/g, "").toLowerCase() }));
    }
    return [];
}

const POINTS_EDITOR_ROLES = ["Owner", "User"];

/** Inline editor for a recruiter's manual points on one criterion (backend stores author, time and reason). */
const PointsEditor = ({ row, candidateId, onChanged, onClose, t }) => {
    const [points, setPoints] = useState(row.manualPoints ?? row.points);
    const [reason, setReason] = useState(row.manualReason ?? "");
    const [isSaving, setIsSaving] = useState(false);
    const n = Number(String(points).replace(",", "."));
    const isValid = points !== "" && !Number.isNaN(n) && n >= 0 && n <= row.maxScore;

    const save = async (value, why) => {
        setIsSaving(true);
        try {
            const updated = await CandidateService.overrideCriterionPoints(candidateId, row.criterionKey, value, why);
            onChanged?.(updated);
            onClose();
        } catch (err) {
            toaster.create({ type: "error", title: extractBusinessErrorMessage(err) ?? t("candidateDetailPage.scoreOverrideError") });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <VStack align="stretch" gap={2} mt={2} p={3} bg="white" border="1px solid" borderColor="purple.200" borderRadius="8px">
            <HStack gap={2}>
                <FormInput type="number" size="sm" w="90px" min={0} max={row.maxScore} step={0.5} value={points} onChange={(e) => setPoints(e.target.value)} />
                <Text fontSize="xs" color="gray.600">/ {formatPoints(row.maxScore)} {t("candidateDetailPage.scorePointsShort")}</Text>
            </HStack>
            <FormTextarea rows={2} resize="vertical" placeholder={t("candidateDetailPage.scoreOverrideReasonPlaceholder")} value={reason} onChange={(e) => setReason(e.target.value)} />
            <HStack justify="space-between" gap={2}>
                {row.manualPoints != null ? (
                    <Button size="xs" variant="ghost" color="gray.600" disabled={isSaving} onClick={() => save(null, null)}>{t("candidateDetailPage.scoreOverrideRestore")}</Button>
                ) : <span />}
                <HStack gap={2}>
                    <Button size="xs" variant="ghost" color="gray.600" disabled={isSaving} onClick={onClose}>{t("candidateDetailPage.changeStatusModalCancel")}</Button>
                    <Button size="xs" variant="primary" loading={isSaving} disabled={!isValid} onClick={() => save(n, reason.trim() || null)}>{t("candidateDetailPage.scoreOverrideSave")}</Button>
                </HStack>
            </HStack>
        </VStack>
    );
};

const ScoreBreakdown = ({ rows, model, candidateId, onChanged }) => {
    const { t, aiLevel } = useScoreLabels();
    const { currentUser } = useAuth();
    const { id: modelId, recruitmentId } = useParams();
    const [editingKey, setEditingKey] = useState(null);
    const canEdit = Boolean(candidateId) && POINTS_EDITOR_ROLES.includes(currentUser?.role);
    const byKey = new Map((model?.criteria ?? []).map((c) => [c.criterionKey, c.scoring]));

    return (
        <VStack align="stretch" gap={3} w="full" pt={1}>
            {rows.map((row) => {
                const missing = row.noData ?? row.capturedValue == null;
                const scale = buildScale(toScoring(row, byKey.get(row.criterionKey)), row, { t, aiLevel });
                const justification = missing ? null : row.aiReason || (row.matchedRule ? t("candidateDetailPage.scoreRuleMatched", { rule: row.matchedRule }) : t("candidateDetailPage.scoreNoRuleMatched"));
                const sourceId = row.sourceRef?.startsWith("interaction:") ? row.sourceRef.slice("interaction:".length) : null;
                const color = missing ? "gray.500" : row.points >= row.maxScore ? "green.700" : row.points > 0 ? "orange.700" : "red.700";
                return (
                    <Box key={row.criterionKey} border="1px solid" borderColor="gray.200" bg="gray.50" borderRadius="10px" p={3}>
                        <HStack justify="space-between" align="flex-start" gap={4}>
                            <Text color="gray.900" fontSize="sm" fontWeight={600}>{row.criterionName}{row.isDisqualifying ? ` (${t("candidateDetailPage.evaluationDisqualifies")})` : ""}</Text>
                            <HStack gap={2} flexShrink={0}>
                                <Text color={color} fontSize="sm" fontWeight={700}>
                                    {formatPoints(row.points)}/{formatPoints(row.maxScore)} {t("candidateDetailPage.scorePointsShort")}
                                </Text>
                                {canEdit && editingKey !== row.criterionKey && (
                                    <Button size="2xs" variant="ghost" color="purple.600" px={1} aria-label={t("candidateDetailPage.scoreOverrideEdit")} onClick={() => setEditingKey(row.criterionKey)}>
                                        <HiOutlinePencilSquare />{t("candidateDetailPage.scoreOverrideEdit")}
                                    </Button>
                                )}
                            </HStack>
                        </HStack>
                        {row.manualPoints != null && (
                            <Text color="purple.700" fontSize="xs" mt={1}>
                                {t("candidateDetailPage.scoreOverrideInfo", { auto: formatPoints(row.autoPoints), by: row.manualBy ?? "-", date: row.manualAt ? new Date(row.manualAt).toLocaleDateString() : "" })}
                                {row.manualReason ? ` — ${row.manualReason}` : ""}
                            </Text>
                        )}
                        {editingKey === row.criterionKey && (
                            <PointsEditor row={row} candidateId={candidateId} onChanged={onChanged} onClose={() => setEditingKey(null)} t={t} />
                        )}
                        <Text color="gray.600" fontSize="xs" mt={1}>
                            {t("candidateDetailPage.scoreAnswer")}:{" "}
                            <Text as="span" color={missing ? "gray.500" : "gray.900"} fontWeight={missing ? 400 : 600}>
                                {missing ? t("candidateDetailPage.scoreNoData") : row.capturedValue}
                            </Text>
                        </Text>
                        {row.condition && (
                            <Text color="gray.600" fontSize="xs" mt={1}>{t("candidateDetailPage.scoreCondition")}: <Text as="span" color="gray.900">{row.condition}</Text></Text>
                        )}
                        <Text color="gray.600" fontSize="xs" mt={1}>
                            {t("candidateDetailPage.evaluationAiReason")}:{" "}
                            <Text as="span" color={missing ? "gray.500" : "gray.900"}>{missing ? t("candidateDetailPage.scoreNoDataReason") : justification}</Text>
                        </Text>
                        {sourceId && modelId && recruitmentId && (
                            <Text fontSize="xs" mt={1}>
                                <RouterLink to={`/models/${modelId}/recruitments/${recruitmentId}/conversations/${sourceId}`}>
                                    <Text as="span" color="purple.600" textDecoration="underline">{t("candidateDetailPage.scoreSource")}</Text>
                                </RouterLink>
                            </Text>
                        )}
                        {scale.length > 0 && (
                            <Box mt={2} border="1px solid" borderColor="gray.200" borderRadius="8px" overflow="hidden" bg="white">
                                <HStack px={3} py={1} bg="gray.100" justify="space-between">
                                    <Text color="gray.500" fontSize="2xs" textTransform="uppercase" letterSpacing="wider">{t("candidateDetailPage.scoreScale")}</Text>
                                    <Text color="gray.500" fontSize="2xs" textTransform="uppercase" letterSpacing="wider">{t("candidateDetailPage.evaluationPoints")}</Text>
                                </HStack>
                                {scale.map((s, i) => (
                                    <HStack
                                        key={i}
                                        px={3}
                                        py={1}
                                        justify="space-between"
                                        bg={s.matched ? "purple.50" : "transparent"}
                                        borderTop="1px solid"
                                        borderColor="gray.100"
                                    >
                                        <Text fontSize="xs" color="gray.800" fontWeight={s.matched ? 700 : 400}>
                                            {s.label}{s.matched ? " ←" : ""}
                                        </Text>
                                        <Text fontSize="xs" color="gray.800" fontWeight={s.matched ? 700 : 400}>{formatPoints(s.points)}</Text>
                                    </HStack>
                                ))}
                            </Box>
                        )}
                        {row.scoringType === "AiMatch" && (
                            <VStack align="flex-start" gap={1} mt={2}>
                                {row.aiLevel && <Text color="gray.600" fontSize="xs">{t("candidateDetailPage.evaluationAiLevel")}: <Text as="span" color="gray.900">{aiLevel(row.aiLevel)}</Text></Text>}
                                {row.aiConfidence != null && <Text color="gray.600" fontSize="xs">{t("candidateDetailPage.evaluationAiConfidence")}: <Text as="span" color="gray.900">{Math.round(row.aiConfidence * 100)}%</Text></Text>}
                                                                {row.aiEvidence && <Text color="gray.600" fontSize="xs">{t("candidateDetailPage.evaluationAiEvidence")}: <Text as="span" color="gray.900">{row.aiEvidence}</Text></Text>}
                            </VStack>
                        )}
                    </Box>
                );
            })}
        </VStack>
    );
};

export default ScoreBreakdown;
